import React, { createContext, useContext, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';
type ThemeSource = 'user' | 'organization';

interface SetThemeOptions {
  source?: ThemeSource;
}

const THEME_STORAGE_KEY = 'theme';
const THEME_LOCK_STORAGE_KEY = 'theme:userLocked';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme, options?: SetThemeOptions) => void;
  effectiveTheme: 'light' | 'dark'; // The actual theme being applied (resolved from system if needed)
  isUserThemeLocked: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const getStoredTheme = (): Theme | null => {
  const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
  if (savedTheme === 'light' || savedTheme === 'dark' || savedTheme === 'system') {
    return savedTheme as Theme;
  }
  return null;
};

const getStoredThemeLock = (): boolean => {
  const savedLock = localStorage.getItem(THEME_LOCK_STORAGE_KEY);

  if (savedLock === '1') {
    return true;
  }

  if (savedLock === '0') {
    return false;
  }

  // Legacy behavior: explicit light/dark likely indicates user preference, while
  // system was the historical default and should remain org-overridable.
  const savedTheme = getStoredTheme();
  return savedTheme === 'light' || savedTheme === 'dark';
};

const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

const getEffectiveTheme = (theme: Theme): 'light' | 'dark' => {
  if (theme === 'system') {
    return getSystemTheme();
  }
  return theme;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    const savedTheme = getStoredTheme();
    if (savedTheme) return savedTheme;
    return 'system';
  });

  const [isUserThemeLocked, setIsUserThemeLocked] = useState<boolean>(() => getStoredThemeLock());

  const [effectiveTheme, setEffectiveTheme] = useState<'light' | 'dark'>(getEffectiveTheme(theme));

  // Handle system preference changes when theme is 'system'
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setEffectiveTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  useEffect(() => {
    // Apply theme to document
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(effectiveTheme);
    root.setAttribute('data-theme', effectiveTheme);
    root.style.colorScheme = effectiveTheme;

    // Save theme preference to local storage
    localStorage.setItem(THEME_STORAGE_KEY, theme);
    localStorage.setItem(THEME_LOCK_STORAGE_KEY, isUserThemeLocked ? '1' : '0');
  }, [theme, effectiveTheme, isUserThemeLocked]);

  // Update effectiveTheme when theme changes
  useEffect(() => {
    setEffectiveTheme(getEffectiveTheme(theme));
  }, [theme]);

  const toggleTheme = () => {
    setIsUserThemeLocked(true);
    setThemeState((prev) => {
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'system';
      return 'light';
    });
  };

  const setTheme = (newTheme: Theme, options?: SetThemeOptions) => {
    setIsUserThemeLocked((options?.source ?? 'user') === 'user');
    setThemeState(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme, effectiveTheme, isUserThemeLocked }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
